// Marketplace Dashboard - Espace client acheteur
// HARMONISÉ avec le design "Mon Espace"
import { useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package, ShoppingBag, Heart, Star, Truck, Clock, CheckCircle,
  ChevronRight, Eye, Euro, TrendingUp, Bell, Plus, X, MessageCircle,
  RefreshCw, User, Calendar, Menu
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { TicketModal } from '../../../components/TicketModal';
import CockpitParticulierBottomNav from '../../../components/layout/CockpitParticulierBottomNav';

function StatCard({ title, value, icon: Icon, color, context, onSelect }) {
  const handleClick = () => {
    console.log('StatCard clicked:', context, title, 'onSelect:', !!onSelect);
    if (context && onSelect) {
      onSelect(context, title);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={handleClick}
      style={{ cursor: context ? 'pointer' : 'default' }}
      className={`bg-white rounded-xl p-4 border border-gray-100 shadow-sm ${context ? 'hover:shadow-md hover:border-orange-200 transition-all' : ''}`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
          <Icon size={20} className="text-white" />
        </div>
        <div className="flex-1">
          <p className="text-lg font-semibold text-gray-900">{value}</p>
          <p className="text-xs text-gray-500">{title}</p>
        </div>
        {context && (
          <ChevronRight size={18} className="text-orange-500" />
        )}
      </div>
    </motion.div>
  );
}

function OrderCard({ order, onClick }) {
  const statusConfig = {
    delivered: { label: 'Livré', bg: 'bg-green-50', text: 'text-green-700', icon: CheckCircle },
    shipping: { label: 'En livraison', bg: 'bg-orange-50', text: 'text-orange-700', icon: Truck },
    processing: { label: 'En préparation', bg: 'bg-amber-50', text: 'text-amber-700', icon: Clock },
  };
  const status = statusConfig[order.status] || statusConfig.processing;
  const StatusIcon = status.icon;

  return (
    <div 
      onClick={onClick}
      className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer active:scale-[0.99]"
    >
      <div className="w-12 h-12 bg-gray-200 rounded-xl overflow-hidden flex-shrink-0">
        {order.image ? (
          <img src={order.image} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package size={18} className="text-gray-400" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-gray-900 truncate">{order.title}</h4>
        <p className="text-xs text-gray-500">{order.seller}</p>
        <div className="flex items-center gap-2 mt-1">
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium flex items-center gap-1 ${status.bg} ${status.text}`}>
            <StatusIcon size={10} />
            {status.label}
          </span>
        </div>
      </div>
      <div className="text-right">
        <p className="text-sm font-semibold text-[#1E3A5F]">{order.price} €</p>
        <p className="text-[10px] text-gray-400">{order.date}</p>
      </div>
    </div>
  );
}

export default function MarketplaceDashboard({ onSelectTool, onOpenMenu }) {
  const navigate = useNavigate();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showCartSuccess, setShowCartSuccess] = useState(false);
  const [showUnavailable, setShowUnavailable] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  
  // Si on est dans le cockpit, utiliser onSelectTool, sinon utiliser navigate
  const handleNavigation = (context, label) => {
    if (onSelectTool) {
      onSelectTool(context, label);
    } else {
      // Fallback pour utilisation hors cockpit
      const routes = {
        'commandes': '/cockpit-particulier/marketplace/commandes',
        'favoris': '/cockpit-particulier/favoris',
        'avis': '/cockpit-particulier/marketplace/avis',
        'deposer': '/cockpit-particulier/deposer',
      };
      if (routes[context]) {
        navigate(routes[context]);
      }
    }
  };
  
  // Contacter le vendeur → Ouvrir le modal Ticket
  const handleContactSeller = () => {
    setShowUnavailable(false);
    setShowTicketModal(true);
  };
  
  // Racheter → Vérifier disponibilité puis ajouter au panier
  const handleRacheter = () => {
    if (!selectedOrder?.available) {
      setShowUnavailable(true);
      setTimeout(() => {
        setShowUnavailable(false);
      }, 2500);
    } else {
      setShowCartSuccess(true);
      setTimeout(() => {
        setShowCartSuccess(false);
        setSelectedOrder(null);
      }, 2000);
    }
  };
  
  const [stats] = useState({
    commandes: 12,
    enCours: 2,
    favoris: 8,
    avisLaisses: 5,
  });

  const [recentOrders] = useState([
    { id: 1, title: 'Perceuse Bosch Professional', seller: 'BricoTools Pro', sellerType: 'pro', available: true, price: 189, status: 'shipping', date: 'Il y a 2 jours', image: 'https://images.unsplash.com/photo-1504148455328-c376907d081c?w=200' },
    { id: 2, title: 'Lot de vis inox 500pcs', seller: 'QuincaillerieExpress', sellerType: 'pro', available: true, price: 24, status: 'delivered', date: 'Il y a 1 semaine', image: null },
    { id: 3, title: 'Niveau laser Bosch', seller: 'OutillagePro', sellerType: 'pro', available: true, price: 299, status: 'processing', date: 'Aujourd\'hui', image: 'https://images.unsplash.com/photo-1572981779307-38b8cabb2407?w=200' },
  ]);

  return (
    <div className="h-full flex flex-col">
      {/* Header - Style identique à Mon Espace */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-3">
            {/* Menu Hamburger */}
            {onOpenMenu && (
              <button
                onClick={onOpenMenu}
                className="md:hidden p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors"
                data-testid="hamburger-menu"
              >
                <Menu size={22} className="text-[#1E3A5F]" />
              </button>
            )}
            <div>
              <h1 className="text-[16px] sm:text-xl font-bold text-[#1e3a5f]">Marketplace</h1>
              <p className="text-xs sm:text-sm text-gray-500">Gère tes achats et tes favoris</p>
            </div>
          </div>
          <Button 
            onClick={() => handleNavigation('deposer', 'Nouvelle annonce')}
            className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl gap-2 h-9 px-4 text-sm font-semibold shadow-sm"
          >
            <Plus size={16} />
            <span className="hidden sm:inline">Nouvelle annonce</span>
          </Button>
        </div>

        {/* Stats rapides */}
        <div className="flex items-center gap-4 mt-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-gray-100">
            <Package size={14} className="text-orange-500" />
            <span className="text-xs font-medium text-gray-600">
              {stats.commandes} commande{stats.commandes > 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <StatCard
          title="Commandes"
          value={stats.commandes}
          icon={Package}
          color="bg-gradient-to-br from-[#1E3A5F] to-[#2D5A8A]"
          context="commandes"
          onSelect={handleNavigation}
        />
        <StatCard
          title="En cours"
          value={stats.enCours}
          icon={Truck}
          color="bg-gradient-to-br from-orange-500 to-orange-600"
        />
        <StatCard
          title="Favoris"
          value={stats.favoris}
          icon={Heart}
          color="bg-gradient-to-br from-rose-500 to-rose-600"
          context="favoris"
          onSelect={handleNavigation}
        />
        <StatCard
          title="Avis laissés"
          value={stats.avisLaisses}
          icon={Star}
          color="bg-gradient-to-br from-amber-500 to-amber-600"
          context="avis"
          onSelect={handleNavigation}
        />
      </div>

      {/* Commandes récentes */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden flex-1">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-[#1E3A5F]">Commandes récentes</h2>
          <button onClick={() => handleNavigation('commandes', 'Mes Commandes')} className="text-xs text-orange-500 font-medium hover:text-orange-600 flex items-center gap-1">
            Voir tout <ChevronRight size={12} />
          </button>
        </div>
        <div className="p-3 space-y-2 overflow-y-auto">
          {recentOrders.length > 0 ? (
            recentOrders.map(order => (
              <OrderCard key={order.id} order={order} onClick={() => setSelectedOrder(order)} />
            ))
          ) : (
            /* État vide */
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-14 h-14 rounded-2xl bg-orange-50 flex items-center justify-center mb-3">
                <Package size={24} className="text-orange-500" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Aucune commande</h3>
              <p className="text-xs text-gray-500 max-w-xs">
                Tes commandes apparaîtront ici une fois que tu auras effectué un achat.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Suggestions - Style harmonisé */}
      <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-100 mt-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <TrendingUp size={18} className="text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-[#1E3A5F] mb-0.5">Découvre nos ventes flash</h3>
            <p className="text-xs text-gray-600 mb-2">
              Profite de réductions jusqu'à -50% sur une sélection de produits.
            </p>
            <Link to="/ventes-flash" className="text-xs text-orange-500 font-semibold hover:text-orange-600">
              Voir les offres →
            </Link>
          </div>
        </div>
      </div>

      {/* Modal Détails de la commande */}
      <AnimatePresence>
        {selectedOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 md:left-[288px] bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedOrder(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-xl"
            >
              {showUnavailable ? (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Package size={32} className="text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Article plus disponible</h3>
                  <p className="text-sm text-gray-500 mb-4">Cet article a déjà été vendu et n'est plus disponible à l'achat.</p>
                  <Button
                    onClick={handleContactSeller}
                    variant="outline"
                    className="mx-auto"
                  >
                    <MessageCircle size={16} className="mr-2" />
                    Contacter le vendeur
                  </Button>
                </div>
              ) : showCartSuccess ? (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={32} className="text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Ajouté au panier !</h3>
                  <p className="text-sm text-gray-500">{selectedOrder?.title} a été ajouté à ton panier.</p>
                </div>
              ) : (
                <>
                  {/* Header avec image */}
                  <div className="relative">
                    <div className="h-48 bg-gray-100 rounded-t-2xl overflow-hidden">
                      {selectedOrder.image ? (
                        <img 
                          src={selectedOrder.image} 
                          alt={selectedOrder.title} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package size={48} className="text-gray-300" />
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => setSelectedOrder(null)}
                      className="absolute top-3 right-3 w-8 h-8 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-sm hover:bg-white transition-colors"
                    >
                      <X size={18} className="text-gray-600" />
                    </button>
                    {/* Badge statut */}
                    <div className="absolute bottom-3 left-3">
                      {(() => {
                        const statusConfig = {
                          delivered: { label: 'Livré', bgColor: 'bg-green-500', icon: CheckCircle },
                          shipping: { label: 'En livraison', bgColor: 'bg-orange-500', icon: Truck },
                          processing: { label: 'En préparation', bgColor: 'bg-amber-500', icon: Clock },
                        };
                        const status = statusConfig[selectedOrder.status] || statusConfig.processing;
                        const StatusIcon = status.icon;
                        return (
                          <span className={`px-3 py-1.5 rounded-full text-xs font-semibold text-white flex items-center gap-1.5 ${status.bgColor}`}>
                            <StatusIcon size={12} />
                            {status.label}
                          </span>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Contenu */}
                  <div className="p-5">
                    <h2 className="text-lg font-semibold text-gray-900 mb-1">{selectedOrder.title}</h2>
                    
                    {/* Prix */}
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-2xl font-bold text-[#1E3A5F]">{selectedOrder.price} €</span>
                    </div>

                    {/* Infos vendeur */}
                    <div className="bg-gray-50 rounded-xl p-4 mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                          <User size={18} className="text-orange-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Vendu par</p>
                          <p className="font-medium text-gray-900">{selectedOrder.seller}</p>
                        </div>
                      </div>
                    </div>

                    {/* Date */}
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
                      <Calendar size={14} />
                      <span>Commandé {selectedOrder.date}</span>
                    </div>

                    {/* Actions */}
                    <div className="space-y-3">
                      <Button
                        onClick={handleContactSeller}
                        className="w-full bg-orange-500 hover:bg-orange-600 text-white flex items-center justify-center gap-2"
                      >
                        <MessageCircle size={16} />
                        Contacter le vendeur
                      </Button>
                      <Button
                        onClick={handleRacheter}
                        variant="outline"
                        className="w-full flex items-center justify-center gap-2"
                      >
                        <RefreshCw size={16} />
                        Racheter cet article
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Ticket (composant réutilisable) */}
      <TicketModal
        isOpen={showTicketModal}
        onClose={() => { setShowTicketModal(false); setSelectedOrder(null); }}
        recipientName={selectedOrder?.seller}
        recipientId={selectedOrder?.id}
        type="buyer"
        onSuccess={() => setSelectedOrder(null)}
      />
      
      {/* Navbar fixe en bas - Mobile uniquement */}
      <CockpitParticulierBottomNav />
    </div>
  );
}
